<template>
  <div class="math-renderer-wrapper">
    <div class="math-renderer" v-html="renderedContent"></div>
  </div>
</template>

<script>
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'MathRenderer',

  props: {
    content: {
      type: String,
      default: '',
    },
  },

  computed: {
    renderedContent() {
      if (!this.content) return ''

      let text = this.content

      try {
        // Array para almacenar las fórmulas renderizadas
        const renderedFormulas = []
        let placeholderIndex = 0

        // IMPORTANTE: Procesar primero los delimitadores display ($$...$$) y \[...\]
        // ANTES de los inline para evitar conflictos

        // 1. Renderizar expresiones en modo display con \[...\]
        text = text.replace(/\\\[([\s\S]+?)\\\]/g, (match, formula) => {
          try {
            const rendered = katex.renderToString(formula, {
              displayMode: true,
              throwOnError: false,
              output: 'html',
            })

            // Guardar en array y reemplazar con placeholder
            const placeholder = `__KATEX_PLACEHOLDER_${placeholderIndex}__`
            renderedFormulas.push(rendered)
            placeholderIndex++
            return placeholder
          } catch (e) {
            console.error('Error rendering \\[...\\] formula:', e, 'Formula:', formula)
            return `<span class="katex-error" title="${e.message}">${match}</span>`
          }
        })

        // 2. Renderizar expresiones en modo display ($$...$$)
        text = text.replace(/\$\$([\s\S]+?)\$\$/g, (match, formula) => {
          try {
            const rendered = katex.renderToString(formula, {
              displayMode: true,
              throwOnError: false,
              output: 'html',
            })

            // Guardar en array y reemplazar con placeholder
            const placeholder = `__KATEX_PLACEHOLDER_${placeholderIndex}__`
            renderedFormulas.push(rendered)
            placeholderIndex++
            return placeholder
          } catch (e) {
            console.error('Error rendering $$...$$ formula:', e, 'Formula:', formula)
            return `<span class="katex-error" title="${e.message}">${match}</span>`
          }
        })

        // 3. Renderizar expresiones en modo inline con \(...\)
        text = text.replace(/\\\(([\s\S]+?)\\\)/g, (match, formula) => {
          try {
            const rendered = katex.renderToString(formula, {
              displayMode: false,
              throwOnError: false,
              output: 'html',
            })

            // Guardar en array y reemplazar con placeholder
            const placeholder = `__KATEX_PLACEHOLDER_${placeholderIndex}__`
            renderedFormulas.push(rendered)
            placeholderIndex++
            return placeholder
          } catch (e) {
            console.error('Error rendering \\(...\\) formula:', e, 'Formula:', formula)
            return `<span class="katex-error" title="${e.message}">${match}</span>`
          }
        })

        // 4. Renderizar expresiones en modo inline ($...$)
        text = text.replace(/\$([^\$\n]+?)\$/g, (match, formula) => {
          try {
            const rendered = katex.renderToString(formula, {
              displayMode: false,
              throwOnError: false,
              output: 'html',
              strict: false,
              trust: true,
            })

            // Guardar en array y reemplazar con placeholder
            const placeholder = `__KATEX_PLACEHOLDER_${placeholderIndex}__`
            renderedFormulas.push(rendered)
            placeholderIndex++
            return placeholder
          } catch (e) {
            console.error('❌ Error rendering $...$ formula:', e, 'Formula:', formula)
            return `<span class="katex-error" title="${e.message}">${match}</span>`
          }
        })

        // 5. Convertir formato de texto (negrita e itálica)
        // Las fórmulas ya están protegidas en placeholders, así que * solo aparece en texto plano
        text = text.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
        text = text.replace(/\*([^*]+?)\*/g, '<em>$1</em>')

        // 6. Convertir saltos de línea a <br>
        text = text.replace(/\n/g, '<br>')

        // 7. Restaurar las fórmulas renderizadas desde los placeholders
        renderedFormulas.forEach((rendered, index) => {
          text = text.replace(`__KATEX_PLACEHOLDER_${index}__`, rendered)
        })

        return text
      } catch (error) {
        console.error('Error in math rendering:', error)
        return this.content
          .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
          .replace(/\n/g, '<br>')
      }
    },
  },
}
</script>

<style>
.math-renderer {
  word-wrap: break-word;
  white-space: normal;
}

/* Ajustar el tamaño de las fórmulas si es necesario */
.math-renderer :deep(.katex) {
  font-size: 1.1em;
}

.math-renderer :deep(.katex-display) {
  margin: 1em 0;
}

.katex-error {
  border: 1px dashed red;
  padding: 2px 4px;
  background-color: #ffebee;
}
</style>
